/**
 * OrderTrackingTab — read-only e-commerce orders tracking for admins.
 *
 * Reads from `GET /api/admin/merchant-orders` (backed by cloudgm_business.orders).
 *
 * Layout mirrors the merchant platform: newest orders on top, status pills,
 * search + filters, per-row expand for full order details.
 */
import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Package, Search, RefreshCw, Filter, Calendar, User, Store,
  DollarSign, Loader2, ChevronDown, ChevronUp, X, Truck, Clock,
  CheckCircle, XCircle, RotateCcw, MapPin, CreditCard,
  Archive, ArchiveRestore, Trash2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../../../utils/authHeaders';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:    { ar: 'قيد الانتظار', en: 'Pending',    color: 'amber',    icon: Clock },
  confirmed:  { ar: 'مؤكد',         en: 'Confirmed',  color: 'blue',     icon: CheckCircle },
  preparing:  { ar: 'قيد التجهيز',  en: 'Preparing',  color: 'purple',   icon: Loader2 },
  // `processing` kept as an alias for legacy records (before we renamed → preparing).
  processing: { ar: 'قيد التجهيز',  en: 'Preparing',  color: 'purple',   icon: Loader2 },
  shipped:    { ar: 'تم الشحن',     en: 'Shipped',    color: 'cyan',     icon: Truck },
  delivered:  { ar: 'تم التوصيل',   en: 'Delivered',  color: 'emerald',  icon: CheckCircle },
  cancelled:  { ar: 'ملغي',         en: 'Cancelled',  color: 'red',      icon: XCircle },
  refunded:   { ar: 'مسترجع',       en: 'Refunded',   color: 'gray',     icon: RotateCcw },
};

const STATUS_TABS = ['all', 'pending', 'confirmed', 'preparing', 'shipped', 'delivered', 'cancelled'];

// Sequential status flow — each state advances ONLY to the next in this list.
// `cancelled` can be selected from any non-terminal state.
const STATUS_SEQUENCE = ['pending', 'confirmed', 'preparing', 'shipped', 'delivered'];
const TERMINAL_STATUSES = new Set(['delivered', 'cancelled', 'refunded']);

function nextStatus(current) {
  const idx = STATUS_SEQUENCE.indexOf(current);
  if (idx < 0 || idx + 1 >= STATUS_SEQUENCE.length) return null;
  return STATUS_SEQUENCE[idx + 1];
}

// Human labels for the "advance" button on each state.
const ADVANCE_LABELS = {
  pending:   { ar: 'تأكيد الطلب', en: 'Confirm order' },
  confirmed: { ar: 'قيد التجهيز', en: 'Start preparing' },
  preparing: { ar: 'تم الشحن',    en: 'Mark shipped' },
  shipped:   { ar: 'تم التوصيل',  en: 'Mark delivered' },
};

const COLOR_CLASSES = {
  amber:   'bg-amber-500/15 text-amber-300 border-amber-500/30',
  blue:    'bg-blue-500/15 text-blue-300 border-blue-500/30',
  purple:  'bg-purple-500/15 text-purple-300 border-purple-500/30',
  cyan:    'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  red:     'bg-red-500/15 text-red-300 border-red-500/30',
  gray:    'bg-white/5 text-white/50 border-white/10',
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${COLOR_CLASSES[meta.color]}`}
      data-testid={`order-status-pill-${status}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {meta.ar}
    </span>
  );
}

export default function OrderTrackingTab({ language = 'ar' }) {
  const isAr = language === 'ar';
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived'
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, per_status: {}, revenue: 0 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [archivingId, setArchivingId] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/merchant-orders/stats`, { headers: getAuthHeaders() });
      setStats(res.data || {});
    } catch (err) {
      console.log('order stats error:', err.message);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      let url;
      if (viewMode === 'archived') {
        params.set('limit', '200');
        url = `${API_URL}/api/admin/merchant-orders/archived?${params.toString()}`;
      } else {
        if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
        params.set('limit', '100');
        url = `${API_URL}/api/admin/merchant-orders?${params.toString()}`;
      }
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setOrders(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل تحميل الطلبات' : 'Failed to load orders'));
    } finally {
      setLoading(false);
    }
  }, [viewMode, statusFilter, search, isAr]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const runProbe = async () => {
    setProbing(true);
    setProbeResult(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/merchant-orders/_diagnostics/probe`,
        { headers: getAuthHeaders() },
      );
      setProbeResult(res.data);
      // Auto-toast the best DB match if any
      const hits = (res.data?.orders_hits_across_cluster || []).filter(h => (h.orders_count || 0) > 0);
      if (hits.length > 0 && hits[0].db !== res.data.active_db) {
        toast.warning(
          isAr
            ? `الطلبات موجودة في قاعدة "${hits[0].db}" (${hits[0].orders_count} طلب) — عدّل DB_BUSINESS`
            : `Orders found in "${hits[0].db}" (${hits[0].orders_count} orders) — update DB_BUSINESS`,
          { duration: 10000 },
        );
      } else if (hits.length > 0) {
        toast.success(isAr ? 'قاعدة البيانات صحيحة — تحقّق من الفلاتر' : 'Correct DB — check filters');
      } else {
        toast.info(isAr ? 'لا توجد طلبات في أي قاعدة بيانات على الكلاستر' : 'No orders in any DB on the cluster');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الفحص' : 'Probe failed'));
    } finally {
      setProbing(false);
    }
  };

  const updateStatus = async (order, newStatus) => {    setUpdatingId(order.id);
    try {
      await axios.patch(
        `${API_URL}/api/admin/merchant-orders/${order.id}/status`,
        { status: newStatus },
        { headers: getAuthHeaders() },
      );
      toast.success(
        isAr
          ? `تم تحديث حالة الطلب #${order.order_number || order.id?.slice(0,8)} إلى «${STATUS_META[newStatus]?.ar || newStatus}»`
          : `Order #${order.order_number || order.id?.slice(0,8)} → ${STATUS_META[newStatus]?.en || newStatus}`,
      );
      // Refresh
      fetchOrders();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل التحديث' : 'Update failed'));
    } finally {
      setUpdatingId(null);
    }
  };

  const archiveOrder = async (order) => {
    setArchivingId(order.id);
    try {
      await axios.post(
        `${API_URL}/api/admin/merchant-orders/${order.id}/archive`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success(
        isAr
          ? `تم حذف الطلب #${order.order_number || order.id?.slice(0,8)} ونقله إلى الأرشيف`
          : `Order #${order.order_number || order.id?.slice(0,8)} moved to archive`,
      );
      setExpandedId(null);
      setArchiveTarget(null);
      fetchOrders();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Delete failed'));
    } finally {
      setArchivingId(null);
    }
  };

  const unarchiveOrder = async (order) => {
    setArchivingId(order.id);
    try {
      await axios.post(
        `${API_URL}/api/admin/merchant-orders/${order.id}/unarchive`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success(
        isAr
          ? `تم استعادة الطلب #${order.order_number || order.id?.slice(0,8)}`
          : `Order #${order.order_number || order.id?.slice(0,8)} restored`,
      );
      fetchOrders();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الاستعادة' : 'Restore failed'));
    } finally {
      setArchivingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/merchant-orders/${deleteTarget.id}`,
        { headers: getAuthHeaders() },
      );
      toast.success(
        isAr
          ? `تم حذف الطلب #${deleteTarget.order_number || deleteTarget.id?.slice(0,8)} نهائياً`
          : `Order #${deleteTarget.order_number || deleteTarget.id?.slice(0,8)} permanently deleted`,
      );
      setDeleteTarget(null);
      fetchOrders();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (value, currency = 'AED') => {
    if (value == null) return '-';
    return `${Number(value).toLocaleString(isAr ? 'ar-SA' : 'en-US', { maximumFractionDigits: 2 })} ${currency}`;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString(isAr ? 'ar-SA' : 'en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div data-testid="order-tracking-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Package className="h-6 w-6 text-orange-400" />
          {isAr ? 'تتبّع الطلبات' : 'Order Tracking'}
          <span className="text-sm font-normal text-[#808080]">
            ({viewMode === 'archived' ? orders.length : (stats.total || 0)})
          </span>
        </h2>
        <button
          onClick={() => { fetchOrders(); fetchStats(); }}
          disabled={loading}
          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10 flex items-center gap-2"
          data-testid="order-tracking-refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {isAr ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      {/* View mode toggle: Active vs Archived */}
      <div className="inline-flex items-center gap-1 bg-[#0a0a0a] p-1 rounded-xl border border-white/10 mb-5">
        <button
          onClick={() => { setViewMode('active'); setExpandedId(null); }}
          className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
            viewMode === 'active'
              ? 'bg-orange-500/20 text-orange-300 border border-orange-500/40'
              : 'text-white/50 hover:text-white border border-transparent'
          }`}
          data-testid="order-view-active"
        >
          <Package className="h-3.5 w-3.5" />
          {isAr ? 'الطلبات النشطة' : 'Active Orders'}
        </button>
        <button
          onClick={() => { setViewMode('archived'); setExpandedId(null); setStatusFilter('all'); }}
          className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
            viewMode === 'archived'
              ? 'bg-white/15 text-white border border-white/20'
              : 'text-white/50 hover:text-white border border-transparent'
          }`}
          data-testid="order-view-archived"
        >
          <Archive className="h-3.5 w-3.5" />
          {isAr ? 'المؤرشفة' : 'Archived'}
        </button>
      </div>

      {/* Stats overview — only in active view */}
      {viewMode === 'active' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="p-4 bg-white/[0.02] rounded-xl border border-white/10" data-testid="order-stats-total">
            <p className="text-xs text-white/50 mb-1">{isAr ? 'إجمالي الطلبات' : 'Total Orders'}</p>
            <p className="text-2xl font-bold text-white">{stats.total || 0}</p>
          </div>
          <div className="p-4 bg-emerald-500/5 rounded-xl border border-emerald-500/20" data-testid="order-stats-revenue">
            <p className="text-xs text-emerald-300/70 mb-1 flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              {isAr ? 'الإيرادات (مدفوع)' : 'Revenue (Paid)'}
            </p>
            <p className="text-lg font-bold text-emerald-300">{formatCurrency(stats.revenue, 'AED')}</p>
          </div>
          <div className="p-4 bg-amber-500/5 rounded-xl border border-amber-500/20">
            <p className="text-xs text-amber-300/70 mb-1">{isAr ? 'قيد الانتظار' : 'Pending'}</p>
            <p className="text-2xl font-bold text-amber-300">{stats.per_status?.pending || 0}</p>
          </div>
          <div className="p-4 bg-cyan-500/5 rounded-xl border border-cyan-500/20">
            <p className="text-xs text-cyan-300/70 mb-1">{isAr ? 'قيد الشحن' : 'Shipped'}</p>
            <p className="text-2xl font-bold text-cyan-300">{stats.per_status?.shipped || 0}</p>
          </div>
        </div>
      )}

      {/* Search + Status filter */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute inset-y-0 my-auto start-3 h-4 w-4 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isAr ? 'بحث برقم الطلب، اسم المشتري، البريد، الجوال، أو المتجر' : 'Search order #, buyer name/email/phone, store...'}
            className="w-full ps-10 pe-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm focus:border-orange-500/50 focus:outline-none"
            data-testid="order-search-input"
          />
        </div>

        {viewMode === 'active' && (
          <div className="flex items-center gap-1 bg-[#0a0a0a] p-1 rounded-xl border border-white/10 overflow-x-auto max-w-full">
            {STATUS_TABS.map((s) => {
              const cnt = s === 'all' ? stats.total : (stats.per_status?.[s] || 0);
              const active = statusFilter === s;
              const meta = STATUS_META[s];
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    active
                      ? (s === 'all' ? 'bg-white/15 text-white' : COLOR_CLASSES[meta.color])
                      : 'text-white/50 hover:text-white'
                  }`}
                  data-testid={`order-status-tab-${s}`}
                >
                  {s === 'all' ? (isAr ? 'الكل' : 'All') : (isAr ? meta.ar : meta.en)}
                  {cnt > 0 && <span className="ms-1 opacity-70">({cnt})</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Orders list */}
      {loading && orders.length === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-white/[0.02] rounded-2xl animate-pulse" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-14 px-6 bg-white/[0.02] rounded-2xl border border-white/10" data-testid="order-empty-state">
          {viewMode === 'archived' ? (
            <>
              <Archive className="h-16 w-16 mx-auto mb-4 opacity-30 text-white/50" />
              <p className="text-lg text-white font-bold">
                {isAr ? 'لا توجد طلبات مؤرشفة' : 'No archived orders'}
              </p>
              <p className="text-sm mt-2 text-white/60 max-w-md mx-auto">
                {isAr
                  ? 'عندما تُؤرشف طلباً من قائمة النشطة، سيظهر هنا. ويمكنك استعادته أو حذفه نهائياً.'
                  : 'Orders you archive from the active list will appear here. You can restore or permanently delete them.'}
              </p>
            </>
          ) : (
            <>
              <Package className="h-16 w-16 mx-auto mb-4 opacity-30 text-orange-400" />
              <p className="text-lg text-white font-bold">
                {isAr ? 'لا توجد طلبات حالياً' : 'No orders yet'}
              </p>
              <p className="text-sm mt-2 text-white/60 max-w-md mx-auto">
                {isAr
                  ? 'ستظهر هنا طلبات التجارة الإلكترونية بمجرد أن يبدأ التجار في استقبال طلبات على منصتهم.'
                  : 'E-commerce orders will appear here as soon as merchants receive orders on their platform.'}
              </p>
            </>
          )}
          {/* Diagnostic block — only in active view */}
          {viewMode === 'active' && stats?.data_source && (
            <details className="mt-6 mx-auto max-w-lg text-start" data-testid="order-empty-diagnostic-details">
              <summary className="cursor-pointer text-[11px] text-white/40 hover:text-white/70 select-none transition">
                {isAr ? '⚙️ تفاصيل تقنية / تشخيص' : '⚙️ Technical details / diagnostic'}
              </summary>
              <div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-3 space-y-1 text-[11px] text-white/60 font-mono" dir="ltr">
              <div className="flex justify-between gap-4">
                <span className="text-white/40">Data source:</span>
                <span className="text-emerald-300">{stats.data_source.db_name}.{stats.data_source.collection}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-white/40">Total records:</span>
                <span className="text-white">{stats.data_source.has_data ? stats.total : 0}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-white/40">Integration:</span>
                <span className={stats.data_source.has_data ? 'text-emerald-300' : 'text-amber-300'}>
                  {stats.data_source.has_data
                    ? (isAr ? '● متصل ويستقبل بيانات' : '● Connected — receiving data')
                    : (isAr ? '● لم تصل بيانات بعد' : '● No data received yet')}
                </span>
              </div>
              {/* Deep diagnostic — scans the whole cluster for the real orders DB */}
              <div className="pt-2 mt-2 border-t border-white/10">
                <button
                  onClick={runProbe}
                  disabled={probing}
                  data-testid="order-probe-btn"
                  className="w-full px-3 py-2 rounded-lg bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 text-orange-300 text-xs font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                  dir={isAr ? 'rtl' : 'ltr'}
                >
                  {probing ? (
                    <><Loader2 className="h-3 w-3 animate-spin" /> {isAr ? 'جارٍ الفحص العميق...' : 'Running deep probe...'}</>
                  ) : (
                    <>🔍 {isAr ? 'فحص عميق (ابحث في كل قواعد البيانات)' : 'Deep probe (scan all cluster DBs)'}</>
                  )}
                </button>
                {probeResult && (
                  <div className="mt-3 space-y-2 text-[10px]" dir="ltr" data-testid="order-probe-result">
                    {/* Active DB + collections */}
                    <div>
                      <p className="text-white/60 mb-1">Active DB: <span className="text-emerald-300">{probeResult.active_db}</span></p>
                      <p className="text-white/40">Collections in this DB:</p>
                      <div className="max-h-24 overflow-y-auto bg-black/40 rounded p-1.5 space-y-0.5">
                        {(probeResult.collections || []).map((c, i) => (
                          <div key={i} className="flex justify-between">
                            <span className={c.name === 'orders' ? 'text-orange-300' : 'text-white/50'}>{c.name}</span>
                            <span className="text-white/40">{c.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Cross-cluster orders hits — THIS is the key insight */}
                    <div>
                      <p className="text-white/40">Orders collections across cluster:</p>
                      <div className="max-h-32 overflow-y-auto bg-black/40 rounded p-1.5 space-y-0.5">
                        {(probeResult.orders_hits_across_cluster || []).map((h, i) => {
                          const isActive = h.db === probeResult.active_db;
                          const hasData = (h.orders_count || 0) > 0;
                          return (
                            <div key={i} className={`flex justify-between ${hasData && !isActive ? 'bg-amber-500/20 px-1 rounded' : ''}`}>
                              <span className={isActive ? 'text-emerald-300' : hasData ? 'text-amber-300 font-bold' : 'text-white/40'}>
                                {isActive ? '▶ ' : '  '}{h.db}
                              </span>
                              <span className={hasData ? 'text-white' : 'text-white/30'}>{h.orders_count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Sample doc keys */}
                    {probeResult.sample_orders_doc_keys?.length > 0 && (
                      <div>
                        <p className="text-white/40">Sample doc keys:</p>
                        <div className="bg-black/40 rounded p-1.5 text-white/60 break-all">
                          {probeResult.sample_orders_doc_keys.join(', ')}
                        </div>
                      </div>
                    )}

                    <p className="text-[9px] text-white/40 italic pt-1" dir={isAr ? 'rtl' : 'ltr'}>
                      {isAr
                        ? '💡 لو ظهرت قاعدة بيانات مضيئة بالأصفر تحتوي على طلبات — قم بتعيين DB_BUSINESS إلى اسمها في متغيّرات البيئة على الإنتاج، ثم أعد النشر.'
                        : '💡 If an amber-highlighted DB shows orders — set DB_BUSINESS to its name in production env vars, then redeploy.'}
                    </p>
                  </div>
                )}
              </div>
              </div>
            </details>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {orders.map((order) => {
            const expanded = expandedId === order.id;
            return (
              <div
                key={order.id}
                className="bg-white/[0.02] rounded-2xl border border-white/10 hover:border-orange-500/30 transition-all overflow-hidden"
                data-testid={`order-row-${order.id}`}
              >
                {/* Compact row */}
                <div
                  className="p-4 flex items-center justify-between gap-4 flex-wrap cursor-pointer"
                  onClick={() => setExpandedId(expanded ? null : order.id)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-orange-500/15 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                      <Package className="h-5 w-5 text-orange-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-white font-bold text-sm">
                          #{order.order_number || order.id?.slice(0, 8)}
                        </span>
                        <StatusPill status={order.status || 'pending'} />
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-white/60 flex-wrap">
                        {order.buyer_name && (
                          <span className="flex items-center gap-1 truncate max-w-[150px]">
                            <User className="h-3 w-3" />
                            {order.buyer_name}
                          </span>
                        )}
                        {order.store_name && (
                          <span className="flex items-center gap-1 truncate max-w-[150px]">
                            <Store className="h-3 w-3" />
                            {order.store_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(order.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-end">
                      <p className="text-[10px] text-white/40">{isAr ? 'الإجمالي' : 'Total'}</p>
                      <p className="text-white font-bold text-sm">{formatCurrency(order.total, order.currency || 'AED')}</p>
                    </div>
                    <button
                      className="p-2 rounded-lg bg-white/5 text-white/60 hover:text-white"
                      data-testid={`order-expand-${order.id}`}
                    >
                      {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Expanded details */}
                <AnimatePresence>
                  {expanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden border-t border-white/10"
                    >
                      <div className="p-4 space-y-4">
                        {/* Buyer + Payment */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div className="p-3 bg-white/[0.02] rounded-lg">
                            <p className="text-[10px] text-white/40 mb-1">{isAr ? 'المشتري' : 'Buyer'}</p>
                            <p className="text-white font-semibold">{order.buyer_name || '-'}</p>
                            <p className="text-xs text-white/60">{order.buyer_email || ''}</p>
                            {order.buyer_phone && (
                              <p className="text-xs text-white/60 font-mono" dir="ltr">{order.buyer_phone}</p>
                            )}
                          </div>
                          <div className="p-3 bg-white/[0.02] rounded-lg">
                            <p className="text-[10px] text-white/40 mb-1 flex items-center gap-1">
                              <CreditCard className="h-3 w-3" />
                              {isAr ? 'الدفع' : 'Payment'}
                            </p>
                            <p className="text-white text-xs">
                              {order.payment_method || (isAr ? 'غير محدد' : 'Not specified')}
                            </p>
                            <p className="text-xs">
                              <span className="text-white/40">{isAr ? 'الحالة: ' : 'Status: '}</span>
                              <span className={order.payment_status === 'paid' ? 'text-emerald-300' : 'text-amber-300'}>
                                {order.payment_status || 'unpaid'}
                              </span>
                            </p>
                          </div>
                        </div>

                        {/* Items */}
                        {Array.isArray(order.items) && order.items.length > 0 && (
                          <div>
                            <p className="text-xs text-white/60 mb-2">{isAr ? 'المنتجات' : 'Items'}</p>
                            <div className="space-y-1.5">
                              {order.items.map((it, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between px-3 py-2 bg-white/[0.02] rounded-lg text-xs"
                                >
                                  <div className="min-w-0 flex-1">
                                    <p className="text-white truncate">{it.name || it.title || '-'}</p>
                                    {it.store_type && (
                                      <p className="text-[10px] text-white/40">{it.store_type}</p>
                                    )}
                                  </div>
                                  <div className="text-end flex-shrink-0 ms-3">
                                    <p className="text-white/80">
                                      {it.qty || 1} × {formatCurrency(it.unit_price, order.currency || 'AED')}
                                    </p>
                                    <p className="text-emerald-300 font-semibold">
                                      {formatCurrency(it.subtotal ?? (it.qty || 1) * (it.unit_price || 0), order.currency || 'AED')}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Totals */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs pt-2 border-t border-white/5">
                          <div>
                            <p className="text-white/40">{isAr ? 'المجموع الفرعي' : 'Subtotal'}</p>
                            <p className="text-white">{formatCurrency(order.subtotal, order.currency || 'AED')}</p>
                          </div>
                          <div>
                            <p className="text-white/40">{isAr ? 'الضريبة' : 'Tax'}</p>
                            <p className="text-white">{formatCurrency(order.tax, order.currency || 'AED')}</p>
                          </div>
                          <div>
                            <p className="text-white/40">{isAr ? 'التوصيل' : 'Delivery'}</p>
                            <p className="text-white">{formatCurrency(order.delivery_fee, order.currency || 'AED')}</p>
                          </div>
                          <div>
                            <p className="text-emerald-300/60">{isAr ? 'الإجمالي' : 'Total'}</p>
                            <p className="text-emerald-300 font-bold">{formatCurrency(order.total, order.currency || 'AED')}</p>
                          </div>
                        </div>

                        {/* Shipping address */}
                        {order.shipping_address && (
                          <div className="p-3 bg-white/[0.02] rounded-lg text-xs">
                            <p className="text-white/40 mb-1 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {isAr ? 'عنوان التوصيل' : 'Shipping Address'}
                            </p>
                            <p className="text-white/80 whitespace-pre-wrap">
                              {typeof order.shipping_address === 'string'
                                ? order.shipping_address
                                : JSON.stringify(order.shipping_address, null, 2)}
                            </p>
                          </div>
                        )}

                        {/* Sequential status actions — only show for non-terminal orders in ACTIVE view */}
                        {viewMode === 'active' && !TERMINAL_STATUSES.has((order.status || 'pending').toLowerCase()) && (
                          <div
                            className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="text-[10px] text-white/50 me-1">
                              {isAr ? 'الإجراءات:' : 'Actions:'}
                            </span>
                            {(() => {
                              const current = (order.status || 'pending').toLowerCase();
                              // "processing" is a legacy alias — treat it as "preparing"
                              const normalized = current === 'processing' ? 'preparing' : current;
                              const next = nextStatus(normalized);
                              const isUpdating = updatingId === order.id;
                              return (
                                <>
                                  {next && (
                                    <button
                                      onClick={() => updateStatus(order, next)}
                                      disabled={isUpdating}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-bold hover:bg-emerald-500/25 disabled:opacity-50 transition"
                                      data-testid={`order-advance-${order.id}`}
                                    >
                                      {isUpdating
                                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        : <CheckCircle className="h-3.5 w-3.5" />}
                                      {isAr ? ADVANCE_LABELS[normalized]?.ar : ADVANCE_LABELS[normalized]?.en}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => updateStatus(order, 'cancelled')}
                                    disabled={isUpdating}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-xs font-bold hover:bg-red-500/25 disabled:opacity-50 transition"
                                    data-testid={`order-cancel-${order.id}`}
                                  >
                                    <XCircle className="h-3.5 w-3.5" />
                                    {isAr ? 'إلغاء' : 'Cancel'}
                                  </button>
                                </>
                              );
                            })()}
                          </div>
                        )}

                        {/* Delete button (soft) — ACTIVE view (any status, including terminal)
                            Opens a confirmation that explains the order will go to Archive first,
                            not deleted permanently. This hides it from Admin / Merchant / Customer. */}
                        {viewMode === 'active' && (
                          <div
                            className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => setArchiveTarget(order)}
                              disabled={archivingId === order.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-xs font-bold hover:bg-red-500/25 disabled:opacity-50 transition"
                              data-testid={`order-delete-soft-${order.id}`}
                            >
                              {archivingId === order.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Trash2 className="h-3.5 w-3.5" />}
                              {isAr ? 'حذف الطلب' : 'Delete Order'}
                            </button>
                            <span className="text-[10px] text-white/40">
                              {isAr
                                ? '— سيُنقل إلى الأرشيف أولاً (يمكن استعادته)'
                                : '— moves to Archive first (recoverable)'}
                            </span>
                          </div>
                        )}

                        {/* Archived view — Restore + Delete */}
                        {viewMode === 'archived' && (
                          <div
                            className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="text-[10px] text-white/50 me-1">
                              {isAr ? 'إجراءات الأرشيف:' : 'Archive actions:'}
                            </span>
                            <button
                              onClick={() => unarchiveOrder(order)}
                              disabled={archivingId === order.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-bold hover:bg-emerald-500/25 disabled:opacity-50 transition"
                              data-testid={`order-unarchive-${order.id}`}
                            >
                              {archivingId === order.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <ArchiveRestore className="h-3.5 w-3.5" />}
                              {isAr ? 'استعادة' : 'Restore'}
                            </button>
                            <button
                              onClick={() => setDeleteTarget(order)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-xs font-bold hover:bg-red-500/25 transition"
                              data-testid={`order-delete-${order.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              {isAr ? 'حذف نهائي' : 'Delete permanently'}
                            </button>
                            {order.archived_at && (
                              <span className="text-[10px] text-white/40 ms-auto" dir="ltr">
                                {isAr ? 'أُرشف:' : 'archived:'} {formatDate(order.archived_at)}
                                {order.archived_by && ` · ${order.archived_by}`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      {/* Soft-delete confirmation dialog (Active view) — moves order to Archive */}
      <AlertDialog
        open={!!archiveTarget}
        onOpenChange={(v) => !v && !archivingId && setArchiveTarget(null)}
      >
        <AlertDialogContent
          className="bg-[#09090b] border-white/10 text-white"
          dir={isAr ? 'rtl' : 'ltr'}
          data-testid="order-archive-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              {isAr ? 'تأكيد حذف الطلب' : 'Confirm order delete'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/70 space-y-2">
              <span className="block">
                {isAr
                  ? <>سيتم حذف الطلب <span className="text-orange-300 font-mono font-bold">#{archiveTarget?.order_number || archiveTarget?.id?.slice(0,8)}</span> من قائمة الطلبات، ومن منصة التجار، ومن منصة العملاء.</>
                  : <>Order <span className="text-orange-300 font-mono font-bold">#{archiveTarget?.order_number || archiveTarget?.id?.slice(0,8)}</span> will be removed from Admin, Merchant, and Customer platforms.</>}
              </span>
              <span className="block text-amber-300">
                {isAr
                  ? 'ℹ️ لن يُحذف نهائياً — سيُنقل إلى تبويب "المؤرشفة" ويمكن استعادته منه. للحذف النهائي، احذفه من قائمة الأرشيف.'
                  : 'ℹ️ Not permanent — it moves to the "Archived" tab and can be restored. For permanent deletion, delete it from the Archived list.'}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              disabled={!!archivingId}
              data-testid="order-archive-cancel"
            >
              {isAr ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); if (archiveTarget) archiveOrder(archiveTarget); }}
              disabled={!!archivingId}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="order-archive-confirm"
            >
              {archivingId ? (
                <><Loader2 className="h-4 w-4 animate-spin me-1" /> {isAr ? 'جارٍ النقل...' : 'Moving...'}</>
              ) : (
                <><Trash2 className="h-4 w-4 me-1" /> {isAr ? 'حذف الطلب' : 'Delete Order'}</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent
          className="bg-[#09090b] border-white/10 text-white"
          dir={isAr ? 'rtl' : 'ltr'}
          data-testid="order-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              {isAr ? 'تأكيد الحذف النهائي' : 'Confirm permanent delete'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/70 space-y-2">
              <span className="block">
                {isAr
                  ? <>سيتم حذف الطلب <span className="text-orange-300 font-mono font-bold">#{deleteTarget?.order_number || deleteTarget?.id?.slice(0,8)}</span> نهائياً من قاعدة البيانات.</>
                  : <>Order <span className="text-orange-300 font-mono font-bold">#{deleteTarget?.order_number || deleteTarget?.id?.slice(0,8)}</span> will be permanently deleted from the database.</>}
              </span>
              <span className="block text-red-300 font-semibold">
                {isAr ? '⚠️ هذا الإجراء لا يمكن التراجع عنه.' : '⚠️ This action cannot be undone.'}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              disabled={deleting}
              data-testid="order-delete-cancel"
            >
              {isAr ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="order-delete-confirm"
            >
              {deleting ? (
                <><Loader2 className="h-4 w-4 animate-spin me-1" /> {isAr ? 'جارٍ الحذف...' : 'Deleting...'}</>
              ) : (
                <><Trash2 className="h-4 w-4 me-1" /> {isAr ? 'حذف نهائي' : 'Delete permanently'}</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
